import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config'
import { useEffect, useState } from 'react'
import { app } from '../config/firebase'

// Mirrors customer/src/services/remoteConfig.ts and EmercadoHub mobile —
// same Firebase Remote Config payload drives all three surfaces so a
// single value flip in the console updates web (customer + voucher
// game) + native at once.
const KEYS = {
  whatsapp: 'contact_whatsapp_number',
  email: 'contact_email',
  instagram: 'contact_instagram',
  facebook: 'contact_facebook',
  tiktok: 'contact_tiktok',
} as const

const DEFAULTS = {
  [KEYS.whatsapp]: '+67075189777',
  [KEYS.email]: 'care@emercadohub.com',
  [KEYS.instagram]: 'emercadohub',
  [KEYS.facebook]: 'emercadohub',
  // TikTok handle (no leading @). Link builder composes
  // https://tiktok.com/@<handle>.
  [KEYS.tiktok]: 'emercado.tl',
}

const rc = getRemoteConfig(app)
rc.settings.minimumFetchIntervalMillis = 60 * 60 * 1000
rc.defaultConfig = DEFAULTS

const listeners = new Set<() => void>()
let started = false

export function initRemoteConfig() {
  if (started) return
  started = true
  fetchAndActivate(rc)
    .then(() => listeners.forEach((fn) => fn()))
    .catch(() => {
      // Network / permission failure → defaults already in place.
    })
}

export interface ContactInfo {
  whatsapp: string
  email: string
  instagram: string
  facebook: string
  tiktok: string
}

export function getContactInfo(): ContactInfo {
  return {
    whatsapp: getValue(rc, KEYS.whatsapp).asString(),
    email: getValue(rc, KEYS.email).asString(),
    instagram: getValue(rc, KEYS.instagram).asString(),
    facebook: getValue(rc, KEYS.facebook).asString(),
    tiktok: getValue(rc, KEYS.tiktok).asString(),
  }
}

export function useContactInfo(): ContactInfo {
  const [, bump] = useState(0)
  useEffect(() => {
    const fn = () => bump((n) => n + 1)
    listeners.add(fn)
    initRemoteConfig()
    return () => {
      listeners.delete(fn)
    }
  }, [])
  return getContactInfo()
}
